import {
  LearnerClassroomAssignmentClassPostsBySectionQuery,
  ThreadDetailQuery,
} from "@outschool/gql-frontend-generated";
import { addParamsToUrl, LearnerRoutes } from "@outschool/routes";
import { navigate, setQueryParams } from "@patched/hookrouter";

import { VIEW } from "./constants";
import {
  BubbleMessageData,
  ConversationListItem,
  DrawerView,
  LearnerAssignmentPublicCommentThread,
  LearnerInfo,
  LearnerPublicCommentThread,
  PostTarget,
  PrivateThreadItem,
  PublicThreadItem,
  PublicThreadTarget,
  ThreadLike,
} from "./types";

type ThreadMessageData = ThreadDetailQuery["thread"]["messages"][number];
type AssignmentCommentData = NonNullable<
  LearnerClassroomAssignmentClassPostsBySectionQuery["section"]
>["assignmentClassPosts"][number]["comments"][number];

export function toBubbleMessageData(
  message: ThreadMessageData,
): BubbleMessageData {
  return {
    uid: message.uid,
    sentAt: message.sentAt,
    sender: {
      uid: message.sender.uid,
      name: message.sender.name,
      photo: message.sender.photo,
      isLearner: message.sender.isLearner,
    },
    messageContent: {
      uid: message.messageContent.uid,
      text: message.messageContent.text,
      attachments: message.messageContent.attachments ?? [],
      video: message.messageContent.video ?? null,
    },
  };
}

export function toBubbleMessageFromAssignmentComment(
  message: AssignmentCommentData,
): BubbleMessageData {
  return {
    uid: message.uid,
    sentAt: message.sentAt,
    sender: {
      uid: message.sender.uid,
      name: message.sender.name,
      photo: message.sender.photo,
      avatar: message.sender.avatar,
      isLearner: message.sender.isLearner,
    },
    messageContent: {
      uid: message.messageContent.uid,
      text: message.messageContent.text,
      attachments: message.messageContent.attachments ?? [],
      video: message.messageContent.video ?? null,
    },
  };
}

export function isGeneralThread(thread: ThreadLike): boolean {
  return !thread?.classPost && !thread?.learningAssignmentClassPost;
}

export function getThreadPostTitle(thread: ThreadLike): string | null {
  return (
    thread?.classPost?.title ??
    thread?.learningAssignmentClassPost?.title ??
    null
  );
}

export function getPostTarget(props: PostTarget): PostTarget {
  if (props.classPostUid) {
    return { classPostUid: props.classPostUid };
  }
  if (props.learningAssignmentClassPostUid) {
    return {
      learningAssignmentClassPostUid: props.learningAssignmentClassPostUid,
    };
  }
  return {};
}

export function getThreadPostTarget(thread: ThreadLike): PostTarget {
  if (thread?.classPost?.uid) {
    return { classPostUid: thread.classPost.uid };
  }
  if (thread?.learningAssignmentClassPost?.uid) {
    return {
      learningAssignmentClassPostUid: thread.learningAssignmentClassPost.uid,
    };
  }
  return {};
}

export function toConversationListItem(
  thread: PrivateThreadItem["thread"],
): PrivateThreadItem {
  return { kind: "thread", thread };
}

export function isSamePublicThreadTarget(
  a: PublicThreadTarget,
  b: PublicThreadTarget,
): boolean {
  if (a.kind === "classPost") {
    return b.kind === "classPost" && a.classPostUid === b.classPostUid;
  }
  return (
    b.kind === "assignment" &&
    a.assignmentClassPostUid === b.assignmentClassPostUid
  );
}

export function toPublicThreadItem(
  thread: LearnerPublicCommentThread,
): PublicThreadItem {
  return {
    kind: "public",
    uid: `public-${thread.uid}`,
    target: { kind: "classPost", classPostUid: thread.classPost.uid },
    postTitle: thread.classPost.title ?? null,
    latestMessage: toPublicThreadLatestMessage(thread.latestComment),
    unreadCount: thread.isRead ? 0 : 1,
  };
}

export function toAssignmentPublicThreadItem(
  thread: LearnerAssignmentPublicCommentThread,
): PublicThreadItem {
  return {
    kind: "public",
    uid: `public-assignment-${thread.uid}`,
    target: {
      kind: "assignment",
      assignmentClassPostUid: thread.assignmentClassPost.uid,
    },
    postTitle: thread.assignmentClassPost.title ?? null,
    latestMessage: toPublicThreadLatestMessage(thread.latestComment),
    unreadCount: thread.isRead ? 0 : 1,
  };
}

function toPublicThreadLatestMessage(
  latestComment: LearnerPublicCommentThread["latestComment"],
): BubbleMessageData {
  return {
    uid: latestComment.uid,
    sentAt: latestComment.sendAt,
    sender: {
      uid: latestComment.sender.uid,
      name: latestComment.sender.name,
      photo: latestComment.sender.photo,
      avatar: latestComment.sender.avatar,
      isLearner: latestComment.sender.isLearner,
    },
    messageContent: {
      uid: latestComment.messageContent.uid,
      text: latestComment.messageContent.text,
      attachments: latestComment.messageContent.attachments ?? [],
      video: latestComment.messageContent.video ?? null,
    },
  };
}

export function getConversationLatestMessage(
  item: ConversationListItem,
): BubbleMessageData | PrivateThreadItem["thread"]["latestMessage"] | null {
  return item.kind === "thread"
    ? item.thread.latestMessage
    : item.latestMessage;
}

export function getConversationKey(item: ConversationListItem): string {
  return item.kind === "thread" ? item.thread.uid : item.uid;
}

export function getConversationTimestamp(
  item: ConversationListItem,
): string | null {
  return getConversationLatestMessage(item)?.sentAt ?? null;
}

export function getConversationTitle(
  item: ConversationListItem,
  teacherName: string,
  t: (key: string, params?: Record<string, string>) => string,
): string {
  const normalizedTeacherName = teacherName?.trim() || t("Teacher");
  if (item.kind === "feedback") {
    return t("Feedback: {{assignmentTitle}}", {
      assignmentTitle: item.assignmentTitle,
    });
  }
  if (item.kind === "public") {
    return item.postTitle?.trim() || t("Class discussion");
  }
  return (
    getThreadPostTitle(item.thread) ??
    t("Ask {{teacherName}} anything", { teacherName: normalizedTeacherName })
  );
}

export function sortConversationsByLatestSentAt(
  items: ConversationListItem[],
): ConversationListItem[] {
  return [...items].sort((a, b) => {
    const aSentAt = getConversationTimestamp(a);
    const bSentAt = getConversationTimestamp(b);
    if (!aSentAt && !bSentAt) {
      return 0;
    }
    if (!aSentAt) {
      return 1;
    }
    if (!bSentAt) {
      return -1;
    }
    return bSentAt.localeCompare(aSentAt);
  });
}

export function getConversationDetailView(
  item: ConversationListItem,
): DrawerView {
  switch (item.kind) {
    case "thread":
      return {
        kind: VIEW.THREAD,
        threadUid: item.thread.uid,
        postTitleOverride: null,
      };
    case "public":
      return {
        kind: VIEW.PUBLIC,
        target: item.target,
        postTitle: item.postTitle,
      };
    case "feedback":
      return {
        kind: VIEW.FEEDBACK,
        feedbackThreadUid: item.uid,
      };
  }
}

// The lessons page selects any lesson item (class post or assignment) via the
// `assignmentUid` query param.
export function lessonBlockPath(sectionUid: string, blockUid: string) {
  return addParamsToUrl(LearnerRoutes.lessonsPath(sectionUid), {
    assignmentUid: blockUid,
  });
}

// hookrouter's `navigate` ignores query strings in the URL and is a no-op for
// the current path, so the query param has to be set separately.
export function navigateToLessonBlock(sectionUid: string, blockUid: string) {
  navigate(LearnerRoutes.lessonsPath(sectionUid));
  setQueryParams({ assignmentUid: blockUid });
}

export function buildLearnerSender(learner: LearnerInfo) {
  return {
    uid: learner.uid,
    name: learner.name ?? "",
    photo: null,
    isLearner: true as const,
  };
}
